// Typed IndexedDB storage layer.
// A single object store "notes", indexed by timestamp for chronological order.

import type { Note } from "./types.js";

const DB_NAME = "note2mail";
const DB_VERSION = 1;
const STORE = "notes";

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("timestamp", "timestamp", { unique: false });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  return dbPromise;
};

const tx = (db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore =>
  db.transaction(STORE, mode).objectStore(STORE);

// All notes, sorted from oldest to newest.
export const getAllNotes = async (): Promise<Note[]> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, "readonly").index("timestamp").getAll();
    req.onsuccess = () => resolve(req.result as Note[]);
    req.onerror = () => reject(req.error);
  });
};

// Create or replace a note.
export const putNote = async (note: Note): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, "readwrite").put(note);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

// Fetch a note by its id, or undefined.
export const getNote = async (id: string): Promise<Note | undefined> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, "readonly").get(id);
    req.onsuccess = () => resolve(req.result as Note | undefined);
    req.onerror = () => reject(req.error);
  });
};
